"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import Container from "ui/Container"
import { sharedColors } from "ui/theme/colors"
import { extractProperty, getNonUmbracoUrl, Link } from "utils"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 32px;
  padding-bottom: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 48px;
    padding-bottom: 48px;
  }
`

const StyledTitle = styled(Typography)<{ component?: React.ElementType }>`
  margin: 0;
`

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
  ${({ theme }) => theme.breakpoints.up("lg")} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const ItemCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid ${sharedColors.borderGray};
`

const ItemTitle = styled(Typography)<{ component?: React.ElementType }>`
  margin: 0;
  font-weight: 600;
`

const ContactRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledLink = styled(Link)`
  color: ${sharedColors.blue};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const OperatingHoursText = styled(Typography)`
  p {
    margin: 0;
  }
`

type Props = {
  data: any
}

export default function OperatingHoursContactInfo({ data }: Props) {
  const title = extractProperty(data, "title")
  const itemsList = extractProperty(data, "itemsList") || []

  const items = itemsList.map((item: any) => ({
    title: extractProperty(item, "title"),
    phoneNumber: extractProperty(item, "phoneNumber")?.[0],
    emailAddress: extractProperty(item, "emailAddress")?.[0],
    operatingHours: extractProperty(item, "operatingHours"),
  }))

  if (!items.length) {
    return null
  }

  return (
    <Container>
      <StyledContainer>
        {title && (
          <StyledTitle variant="h2" component="h2">
            {title}
          </StyledTitle>
        )}
        <ItemsGrid>
          {items.map((item: any, index: number) => (
            <ItemCard key={index}>
              {item.title && (
                <ItemTitle variant="h3" component="h3">
                  {item.title}
                </ItemTitle>
              )}
              <ContactRow>
                {item.phoneNumber && (
                  <StyledLink
                    href={getNonUmbracoUrl(item.phoneNumber.url) || "#"}
                  >
                    <Typography variant="body2">
                      {item.phoneNumber.name}
                    </Typography>
                  </StyledLink>
                )}
                {item.emailAddress && (
                  <StyledLink
                    href={getNonUmbracoUrl(item.emailAddress.url) || "#"}
                  >
                    <Typography variant="body2">
                      {item.emailAddress.name}
                    </Typography>
                  </StyledLink>
                )}
              </ContactRow>
              {item.operatingHours && (
                <OperatingHoursText
                  variant="body2"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: item.operatingHours }}
                />
              )}
            </ItemCard>
          ))}
        </ItemsGrid>
      </StyledContainer>
    </Container>
  )
}
